// Dashboard.js - Complete redesign with dark green + white theme

import ImportPatientsPanel from './ImportPatientsPanel';
import EditablePersonalizedLine from './EditablePersonalizedLine';
import ScriptManager, { applyScriptToPatient } from './ScriptManager';
import BulkReminderQueue from './BulkReminderQueue';
import React, { useState, useEffect, useCallback } from 'react';
import FinalCTA from './FinalCTA';
import {
  collection, addDoc, getDocs, updateDoc, doc, query, orderBy, onSnapshot, writeBatch
} from 'firebase/firestore';
import { format, differenceInDays } from 'date-fns';
import { Settings, Plus, FileText, LogOut, AlertCircle, Upload, MessageCircle, Search, Users, Calendar, Phone, Mail, Clock, ChevronRight, TrendingUp } from 'lucide-react';

import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { db } from '../firebase';
import {
  DEMO_PATIENTS, sortPatients, getPatientStatus, calcDaysOverdue
} from '../utils/data';
import { StatusBadge, TreatmentPill } from './Badges';
import AddPatientPanel from './AddPatientPanel';
import MessagePanel from './MessagePanel';
import PatientDetailPanel from './PatientDetailPanel';
import SettingsPanel from './SettingsPanel';
import SkeletonTable from './SkeletonTable';
import { createPatientStore } from './PatientStore';

export default function Dashboard() {
  const { currentUser, practiceData, isDemo, logout } = useAuth();
  const { addToast } = useToast();

  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [editPatient, setEditPatient] = useState(null);
  const [messagePatient, setMessagePatient] = useState(null);
  const [detailPatient, setDetailPatient] = useState(null);
  const [showSettings, setShowSettings] = useState(false);
  const [search, setSearch] = useState('');
  const [showQueue, setShowQueue] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [activeTab, setActiveTab] = useState('patients'); // 'patients' | 'reminders'
  const [showScriptManager, setShowScriptManager] = useState(false);
  const [showFinalCTA, setShowFinalCTA] = useState(true); // ← ADD THIS LINE

  const practiceName = practiceData?.practiceName || 'Your Practice';

  async function handleImportPatients(rows) {
    if (isDemo) {
      const store = createPatientStore({ isDemo: true, practiceUid: null });
      await store.addPatientsBulk(rows);
      const updated = store.getSnapshotSync();
      if (updated) setPatients(updated);
      addToast(`${rows.length} patients imported`);
      setShowImport(false);
      return;
    }
    try {
      const batch = writeBatch(db);
      const colRef = collection(db, 'practices', currentUser.uid, 'patients');
      rows.forEach(r => {
        const ref = doc(colRef);
        batch.set(ref, { ...r, createdAt: new Date().toISOString() });
      });
      await batch.commit();
      addToast(`${rows.length} patients imported`);
      setShowImport(false);
    } catch (e) {
      addToast('Import failed — please check your file and try again', 'error');
    }
  }

  async function handleUpdatePatient(id, updates) {
  try {
    if (isDemo) {
      const store = createPatientStore({ isDemo: true, practiceUid: null });
      await store.updatePatient(id, updates);
      const updated = store.getSnapshotSync();
      if (updated) setPatients(updated);
      addToast('Personalized line updated! ✨', 'success');
      return;
    }
    await updateDoc(doc(db, 'practices', currentUser.uid, 'patients', id), updates);
    addToast('Personalized line updated! ✨', 'success');
  } catch (e) {
    addToast('Failed to update personalized line', 'error');
  }
}

useEffect(() => {
  async function loadChosenScript() {
    // First check localStorage (fast)
    const localScript = localStorage.getItem('recallready_selected_script');
    if (localScript) {
      try {
        const script = JSON.parse(localScript);
        // Use it
        return;
      } catch (e) {}
    }
    
    // If not in localStorage, check Firebase
    if (currentUser && !isDemo) {
      try {
        const settingsRef = doc(db, 'practices', currentUser.uid, 'settings', 'preferences');
        const snapshot = await getDoc(settingsRef);
        if (snapshot.exists() && snapshot.data().selectedScript) {
          localStorage.setItem('recallready_selected_script', JSON.stringify(snapshot.data().selectedScript));
        }
      } catch (e) {}
    }
  }
  
  loadChosenScript();
}, [currentUser, isDemo]);

  useEffect(() => {
    if (isDemo) {
      const store = createPatientStore({ isDemo: true, practiceUid: null });
      store.subscribe((data) => {
        setPatients(data);
        setLoading(false);
      });
      return () => {};
    }
    const q = query(collection(db, 'practices', currentUser.uid, 'patients'));
    const unsub = onSnapshot(q, (snap) => {
      const data = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      setPatients(data);
      setLoading(false);
    }, (err) => {
      console.error(err);
      addToast('Error loading patients', 'error');
      setLoading(false);
    });
    return unsub;
  }, [currentUser, isDemo]);

  const sorted = sortPatients(patients).filter(p => {
    if (!search) return true;
    const q = search.toLowerCase();
    return `${p.firstName} ${p.lastName}`.toLowerCase().includes(q) ||
      (p.treatmentType || '').toLowerCase().includes(q);
  });

  const total = patients.length;
  const overdue = patients.filter(p => getPatientStatus(p) === 'Overdue').length;
  const dueSoon = patients.filter(p => getPatientStatus(p) === 'Due Soon').length;
  const contactedThisMonth = patients.filter(p => {
    if (!p.lastContacted) return false;
    return differenceInDays(new Date(), new Date(p.lastContacted)) <= 30;
  }).length;

  async function handleSavePatient(data) {
    try {
      if (isDemo) {
        const store = createPatientStore({ isDemo: true, practiceUid: null });
        await store.addPatient(data);
        const updated = store.getSnapshotSync();
        if (updated) setPatients(updated);
        addToast(editPatient ? 'Patient updated' : 'Patient added');
        setShowAdd(false);
        setEditPatient(null);
        return;
      }
      if (editPatient) {
        const { id, ...rest } = data;
        await updateDoc(doc(db, 'practices', currentUser.uid, 'patients', id), rest);
        addToast('Patient updated');
      } else {
        await addDoc(collection(db, 'practices', currentUser.uid, 'patients'), {
          ...data, visitHistory: [], lastContacted: null, createdAt: new Date().toISOString(),
        });
        addToast('Patient added');
      }
      setShowAdd(false);
      setEditPatient(null);
    } catch (e) {
      addToast('Failed to save patient', 'error');
    }
  }

  const reminderQueue = patients
    .filter(p => {
      const status = getPatientStatus(p);
      if (status !== 'Overdue' && status !== 'Due Soon') return false;
      if (p.lastContacted && differenceInDays(new Date(), new Date(p.lastContacted)) < 14) return false;
      return true;
    })
    .sort((a, b) => {
      const aDays = a.recallDate ? calcDaysOverdue(a.recallDate) : 0;
      const bDays = b.recallDate ? calcDaysOverdue(b.recallDate) : 0;
      return bDays - aDays;
    });

  async function handleMarkContacted(id) {
    try {
      if (isDemo) {
        const today = format(new Date(), 'yyyy-MM-dd');
        const store = createPatientStore({ isDemo: true, practiceUid: null });
        await store.updatePatient(id, {
          lastContacted: today,
          visitHistory: [
            ...(patients.find(p => p.id === id)?.visitHistory || []),
            { date: today, note: 'Marked as contacted via RecallReady.' }
          ]
        });
        const updated = store.getSnapshotSync();
        if (updated) setPatients(updated);
        addToast('Marked as contacted');
        return;
      }
      const today = format(new Date(), 'yyyy-MM-dd');
      await updateDoc(doc(db, 'practices', currentUser.uid, 'patients', id), {
        lastContacted: today,
        visitHistory: [
          ...(patients.find(p => p.id === id)?.visitHistory || []),
          { date: today, note: 'Marked as contacted via RecallReady.' }
        ]
      });
      addToast('Marked as contacted');
    } catch {
      addToast('Failed to update patient', 'error');
    }
  }

  async function handleLogVisit(id, note) {
    try {
      if (isDemo) {
        const today = format(new Date(), 'yyyy-MM-dd');
        const p = patients.find(p => p.id === id);
        const newHistory = [...(p?.visitHistory || []), { date: today, note }];
        const store = createPatientStore({ isDemo: true, practiceUid: null });
        await store.updatePatient(id, {
          visitHistory: newHistory,
          lastContacted: today,
        });
        const updated = store.getSnapshotSync();
        if (updated) setPatients(updated);
        addToast('Visit logged');
        return;
      }
      const today = format(new Date(), 'yyyy-MM-dd');
      const p = patients.find(p => p.id === id);
      const newHistory = [...(p?.visitHistory || []), { date: today, note }];
      await updateDoc(doc(db, 'practices', currentUser.uid, 'patients', id), {
        visitHistory: newHistory, lastContacted: today,
      });
      addToast('Visit logged');
    } catch {
      addToast('Failed to log visit', 'error');
    }
  }

  function handleEditFromDetail(patient) {
    setDetailPatient(null);
    setEditPatient(patient);
    setShowAdd(true);
  }

  function handleUseScript(scriptData) {
  const { script, applyToAll } = scriptData;
  
  // Save selected script to localStorage for quick access
  localStorage.setItem('recallready_selected_script', JSON.stringify(script));
  
  // Also save to Firebase for persistence across devices
  if (currentUser && !isDemo) {
    try {
      const settingsRef = doc(db, 'practices', currentUser.uid, 'settings', 'preferences');
      setDoc(settingsRef, {
        selectedScriptId: script.id,
        selectedScript: script,
        updatedAt: new Date().toISOString(),
      }, { merge: true });
    } catch (error) {
      console.error('Error saving selected script:', error);
    }
  }
  
  if (applyToAll) {
    const messages = patients.map(p => {
      const line = p.personalizedLine || `We've been thinking about you and wanted to check in.`;
      const message = applyScriptToPatient(script, p, practiceName, line);
      return { patient: p, message };
    });
    
    addToast(`✅ Script "${script.name}" applied to all ${patients.length} patients!`, 'success');
  } else {
    addToast(`Script "${script.name}" is ready to use!`, 'success');
  }
}


  const stats = [
    { label: 'Total Patients', value: total, icon: Users, color: '#1a3a3a' },
    { label: 'Overdue', value: overdue, icon: Clock, color: '#c0392b' },
    { label: 'Due Soon', value: dueSoon, icon: Calendar, color: '#d4a843' },
    { label: 'Contacted', value: contactedThisMonth, icon: Phone, color: '#2d6a5e' },
  ];

  return (
    <div style={pageStyle}>
      {isDemo && (
        <div style={demoBannerStyle}>
          <AlertCircle size={14} />
          Demo mode — data is saved to your browser session
        </div>
      )}

      {/* Top bar */}
      <div style={topBarStyle}>
        <div style={logoMarkStyle}>
          <div style={logoIconWrapper}>
            <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
              <rect width="28" height="28" rx="6" fill="#1a3a3a"/>
              <path d="M8 12c0-3 2.5-5 5.5-5h5c3 0 5.5 2 5.5 5 0 3.5-2.5 6-5.5 6 .5 1.5 2 3 3.5 4.5H9.5c1-1.5 2.5-3 3-4.5-3-.5-4.5-3-4.5-6z" fill="#e8f0ee"/>
              <circle cx="14" cy="13" r="2" fill="#1a3a3a" opacity="0.15"/>
            </svg>
          </div>
          <span style={practiceNameStyle}>{practiceName}</span>
        </div>
        <div style={topBarRightStyle}>
          <span style={userEmailStyle}>{currentUser?.email}</span>
          <button onClick={() => setShowSettings(true)} style={iconBtnStyle} title="Settings">
            <Settings size={18} />
          </button>
          <button onClick={logout} style={{ ...iconBtnStyle, color: '#b85c4a' }} title="Sign out">
            <LogOut size={18} />
          </button>
          <button 
  onClick={() => setShowScriptManager(true)} 
  style={{ 
    ...iconBtnStyle, 
    fontStyle: 'italic',
    fontWeight: 600,
    color: '#2D9B8A',
  }} 
  title="My Scripts"
>
  <FileText size={18} />
  My Script
</button>
        </div>
      </div>

      <div style={contentStyle}>
        {/* Stats grid */}
        <div style={statsGridStyle}>
          {stats.map((stat, i) => (
            <div key={i} style={statCardStyle}>
              <div style={statIconWrapper(stat.color)}>
                <stat.icon size={20} color="#fff" />
              </div>
              <div style={statContentStyle}>
                <div style={statValueStyle}>{stat.value}</div>
                <div style={statLabelStyle}>{stat.label}</div>
              </div>
            </div>
          ))}
        </div>

        {/* Main content area */}
        <div style={mainCardStyle}>
          {/* Card header */}
          <div style={cardHeaderStyle}>
  <div style={cardHeaderLeftStyle}>
    <h2 style={cardTitleStyle}>Patients</h2>
    <span style={patientCountStyle}>{total} total</span>
  </div>
  
  <div style={cardActionsStyle}>
    {/* My Script - Left of search */}
    <button 
      onClick={() => setShowScriptManager(true)} 
      style={{ 
        ...secondaryBtnStyle,
        fontStyle: 'italic',
        fontWeight: 600,
        color: '#2D9B8A',
        borderColor: '#2D9B8A',
        display: 'flex',
        alignItems: 'center',
        gap: 6,
        padding: '8px 16px',
      }} 
      title="My Scripts"
    >
      <FileText size={16} />
      My Script
    </button>
    
    {/* Search */}
    <div style={searchWrapperStyle}>
      <Search size={16} style={searchIconStyle} />
      <input
        style={searchStyle}
        placeholder="Search patients..."
        value={search}
        onChange={e => setSearch(e.target.value)}
      />
    </div>
    
    {/* Import */}
    <button onClick={() => setShowImport(true)} style={secondaryBtnStyle}>
      <Upload size={16} />
      Import
    </button>
    
    {/* Add patient */}
    <button 
      onClick={() => { setEditPatient(null); setShowAdd(true); }} 
      style={primaryBtnStyle}
    >
      <Plus size={16} />
      Add patient
    </button>
    
    {/* Remind */}
    {reminderQueue.length > 0 && (
      <button 
        onClick={() => setShowQueue(true)} 
        style={whatsappBtnStyle}
      >
        <MessageCircle size={16} />
        Remind ({reminderQueue.length})
      </button>
    )}
  </div>
</div>

          {/* Table */}
          <div style={tableWrapStyle}>
            {loading ? (
              <SkeletonTable />
            ) : sorted.length === 0 ? (
              <EmptyState onAdd={() => setShowAdd(true)} hasSearch={!!search} />
            ) : (
              <table style={tableStyle}>
                <thead>
                  <tr style={tableHeadRowStyle}>
                    {['Patient', 'Last visit', 'Treatment', 'Recall due', 'Personalized Line', 'Status', ''].map(h => (
  <th key={h} style={tableHeadStyle}>{h}</th>
))}

                  </tr>
                </thead>
                <tbody>
  {sorted.map(p => (
    <PatientRow
      key={p.id}
      patient={p}
      onRowClick={() => setDetailPatient(p)}
      onMessage={e => { e.stopPropagation(); setMessagePatient(p); }}
      onMarkContacted={e => { e.stopPropagation(); handleMarkContacted(p.id); }}
      onUpdatePatient={handleUpdatePatient}  // ← ADD THIS LINE
    />
  ))}
</tbody>
              </table>
            )}
          </div>
        </div>
      </div>

      {showAdd && (
        <AddPatientPanel
          editPatient={editPatient}
          onClose={() => { setShowAdd(false); setEditPatient(null); }}
          onSave={handleSavePatient}
        />
      )}
      {showQueue && (
        <BulkReminderQueue
          queue={reminderQueue}
          practiceName={practiceName}
          onClose={() => setShowQueue(false)}
          onMarkContacted={handleMarkContacted}
        />
      )}
      {showImport && (
        <ImportPatientsPanel
          onClose={() => setShowImport(false)}
          onImport={handleImportPatients}
        />
      )}
      {messagePatient && (
        <MessagePanel
          patient={messagePatient}
          practiceName={practiceName}
          onClose={() => setMessagePatient(null)}
          onMarkContacted={handleMarkContacted}
        />
      )}
      {showScriptManager && (
  <ScriptManager
    onClose={() => setShowScriptManager(false)}
    onUseScript={handleUseScript}
    practiceName={practiceName}
  />
)}
      {detailPatient && (
        <PatientDetailPanel
          patient={detailPatient}
          onClose={() => setDetailPatient(null)}
          onEdit={handleEditFromDetail}
          onLogVisit={handleLogVisit}
        />
      )}
      {showSettings && <SettingsPanel onClose={() => setShowSettings(false)} />}
        {showFinalCTA && (
        <FinalCTA onContinue={() => setShowFinalCTA(false)} />
      )}
    </div>
  );
}

function PatientRow({ patient, onRowClick, onMessage, onMarkContacted, onUpdatePatient }) {
  const status = getPatientStatus(patient);
  const daysOverdue = patient.recallDate ? calcDaysOverdue(patient.recallDate) : 0;
  const isOverdue = daysOverdue > 0 && status === 'Overdue';

  return (
    <tr
      onClick={onRowClick}
      style={{
        ...tableRowStyle,
        background: isOverdue ? '#fdf6f5' : 'transparent',
      }}
      onMouseEnter={e => e.currentTarget.style.background = '#f5f8f7'}
      onMouseLeave={e => e.currentTarget.style.background = isOverdue ? '#fdf6f5' : 'transparent'}
    >
      <td style={tableCellStyle}>
        <div style={patientNameStyle}>
          <span style={nameTextStyle}>{patient.firstName} {patient.lastName}</span>
          {patient.phone && <span style={phoneBadgeStyle}>{patient.phone}</span>}
        </div>
      </td>
      <td style={tableCellStyle}>
        <span style={cellTextStyle}>
          {patient.lastVisit ? format(new Date(patient.lastVisit), 'dd MMM yyyy') : '—'}
        </span>
      </td>
      <td style={tableCellStyle}>
        <TreatmentPill type={patient.treatmentType} />
      </td>
      <td style={tableCellStyle}>
        <span style={{
          ...cellTextStyle,
          color: isOverdue ? '#b85c4a' : '#1a3a3a',
          fontWeight: isOverdue ? 600 : 400,
        }}>
          {patient.recallDate ? format(new Date(patient.recallDate), 'dd MMM yyyy') : '—'}
          {isOverdue && <span style={overdueBadgeStyle}>+{daysOverdue}d</span>}
        </span>
      </td>
      {/* NEW: Personalized Line Column */}
      <td style={tableCellStyle}>
        <EditablePersonalizedLine 
          patient={patient} 
          onUpdate={onUpdatePatient} 
        />
      </td>
      <td style={tableCellStyle}>
        <StatusBadge status={status} />
      </td>
      <td style={tableCellStyle} onClick={e => e.stopPropagation()}>
        <div style={actionGroupStyle}>
          <button onClick={onMessage} style={actionBtnStyle}>
            Message
          </button>
          {status !== 'Contacted' && (
            <button onClick={onMarkContacted} style={contactedBtnStyle}>
              ✓ Contacted
            </button>
          )}
        </div>
      </td>
    </tr>
  );
}

function EmptyState({ onAdd, hasSearch }) {
  if (hasSearch) return (
    <div style={emptyStyle}>
      <p style={emptyTextStyle}>No patients match your search.</p>
    </div>
  );
  return (
    <div style={emptyStyle}>
      <div style={emptyIconStyle}>
        <Users size={48} color="#b5c8c4" />
      </div>
      <h3 style={emptyTitleStyle}>No patients yet</h3>
      <p style={emptyDescStyle}>Add your first patient to get started with recall tracking.</p>
      <button onClick={onAdd} style={primaryBtnStyle}>
        <Plus size={16} />
        Add first patient
      </button>
    </div>
  );
}

// ========== STYLES ==========

// Page
const pageStyle = {
  minHeight: '100vh',
  background: '#f0f5f3',
  fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
};

const demoBannerStyle = {
  background: '#1a3a3a',
  color: '#e8f0ee',
  fontSize: 12,
  textAlign: 'center',
  padding: '6px 16px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 6,
  fontFamily: "'Inter', sans-serif",
};

// Top bar
const topBarStyle = {
  background: '#ffffff',
  borderBottom: '1px solid #e8ecea',
  padding: '0 40px',
  height: 64,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  position: 'sticky',
  top: 0,
  zIndex: 100,
};

const logoMarkStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: 12,
};

const logoIconWrapper = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
};

const practiceNameStyle = {
  fontFamily: "'Inter', sans-serif",
  fontWeight: 600,
  fontSize: 16,
  color: '#1a3a3a',
  letterSpacing: '-0.3px',
};

const topBarRightStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
};

const userEmailStyle = {
  fontSize: 13,
  color: '#7a9a92',
  fontFamily: "'Inter', sans-serif",
  marginRight: 8,
};

const iconBtnStyle = {
  background: 'none',
  border: 'none',
  cursor: 'pointer',
  color: '#5a7a72',
  padding: 8,
  display: 'flex',
  alignItems: 'center',
  borderRadius: 6,
  transition: 'background 0.15s',
};

// Content
const contentStyle = {
  padding: '32px 40px',
  maxWidth: 1400,
  margin: '0 auto',
};

// Stats
const statsGridStyle = {
  display: 'grid',
  gridTemplateColumns: 'repeat(4, 1fr)',
  gap: 16,
  marginBottom: 24,
};

const statCardStyle = {
  background: '#ffffff',
  borderRadius: 12,
  padding: '20px 24px',
  display: 'flex',
  alignItems: 'center',
  gap: 16,
  border: '1px solid #e8ecea',
  transition: 'border-color 0.2s, box-shadow 0.2s',
};

const statIconWrapper = (color) => ({
  width: 44,
  height: 44,
  borderRadius: 10,
  background: color,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
});

const statContentStyle = {
  flex: 1,
};

const statValueStyle = {
  fontSize: 28,
  fontWeight: 700,
  color: '#1a3a3a',
  lineHeight: 1.2,
  fontFamily: "'Inter', sans-serif",
  letterSpacing: '-0.5px',
};

const statLabelStyle = {
  fontSize: 12,
  color: '#7a9a92',
  fontWeight: 500,
  marginTop: 2,
  fontFamily: "'Inter', sans-serif",
};

// Main card
const mainCardStyle = {
  background: '#ffffff',
  borderRadius: 12,
  border: '1px solid #e8ecea',
  overflow: 'hidden',
  boxShadow: '0 1px 3px rgba(26, 58, 58, 0.06)',
};

const cardHeaderStyle = {
  padding: '20px 24px',
  borderBottom: '1px solid #e8ecea',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  flexWrap: 'wrap',
  gap: 12,
};

const cardHeaderLeftStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: 12,
};

const cardTitleStyle = {
  fontSize: 18,
  fontWeight: 600,
  color: '#1a3a3a',
  margin: 0,
  fontFamily: "'Inter', sans-serif",
  letterSpacing: '-0.3px',
};

const patientCountStyle = {
  fontSize: 13,
  color: '#7a9a92',
  fontWeight: 400,
  fontFamily: "'Inter', sans-serif",
};

const cardActionsStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  flexWrap: 'wrap',
};

// Search
const searchWrapperStyle = {
  position: 'relative',
  display: 'flex',
  alignItems: 'center',
};

const searchIconStyle = {
  position: 'absolute',
  left: 12,
  color: '#b5c8c4',
};

const searchStyle = {
  padding: '8px 12px 8px 36px',
  border: '1px solid #e0e6e4',
  borderRadius: 8,
  fontSize: 13,
  fontFamily: "'Inter', sans-serif",
  color: '#1a3a3a',
  background: '#f8faf9',
  outline: 'none',
  width: 220,
  transition: 'border-color 0.2s, box-shadow 0.2s',
};

// Buttons
const primaryBtnStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: 6,
  padding: '8px 16px',
  background: '#1a3a3a',
  color: '#ffffff',
  border: 'none',
  borderRadius: 8,
  cursor: 'pointer',
  fontSize: 13,
  fontWeight: 500,
  fontFamily: "'Inter', sans-serif",
  transition: 'background 0.2s, transform 0.1s',
};

const secondaryBtnStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: 6,
  padding: '8px 16px',
  background: 'transparent',
  color: '#1a3a3a',
  border: '1px solid #dce3e0',
  borderRadius: 8,
  cursor: 'pointer',
  fontSize: 13,
  fontWeight: 500,
  fontFamily: "'Inter', sans-serif",
  transition: 'background 0.2s',
};

const whatsappBtnStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: 6,
  padding: '8px 16px',
  background: '#25a55a',
  color: '#ffffff',
  border: 'none',
  borderRadius: 8,
  cursor: 'pointer',
  fontSize: 13,
  fontWeight: 500,
  fontFamily: "'Inter', sans-serif",
  transition: 'background 0.2s',
};

// Table
const tableWrapStyle = {
  overflowX: 'auto',
};

const tableStyle = {
  width: '100%',
  borderCollapse: 'collapse',
  minWidth: 780,
};

const tableHeadRowStyle = {
  background: '#f8faf9',
  borderBottom: '1px solid #e8ecea',
};

const tableHeadStyle = {
  padding: '12px 16px',
  textAlign: 'left',
  fontSize: 11,
  fontWeight: 600,
  color: '#7a9a92',
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
  fontFamily: "'Inter', sans-serif",
};

const tableRowStyle = {
  borderBottom: '1px solid #f0f3f2',
  cursor: 'pointer',
  transition: 'background 0.15s',
};

const tableCellStyle = {
  padding: '14px 16px',
  verticalAlign: 'middle',
};

const patientNameStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  flexWrap: 'wrap',
};

const nameTextStyle = {
  fontSize: 14,
  fontWeight: 500,
  color: '#1a3a3a',
  fontFamily: "'Inter', sans-serif",
};

const phoneBadgeStyle = {
  fontSize: 11,
  color: '#7a9a92',
  background: '#f0f5f3',
  padding: '2px 10px',
  borderRadius: 12,
  fontFamily: "'Inter', sans-serif",
};

const cellTextStyle = {
  fontSize: 13,
  color: '#3a5a52',
  fontFamily: "'Inter', sans-serif",
};

const overdueBadgeStyle = {
  marginLeft: 8,
  fontSize: 11,
  fontWeight: 600,
  color: '#b85c4a',
  background: '#fdf0ed',
  padding: '1px 8px',
  borderRadius: 10,
};

const actionGroupStyle = {
  display: 'flex',
  gap: 6,
};

const actionBtnStyle = {
  padding: '4px 12px',
  background: 'transparent',
  color: '#3a5a52',
  border: '1px solid #dce3e0',
  borderRadius: 6,
  cursor: 'pointer',
  fontSize: 12,
  fontWeight: 500,
  fontFamily: "'Inter', sans-serif",
  transition: 'background 0.15s',
};

const contactedBtnStyle = {
  padding: '4px 12px',
  background: '#ecf5f2',
  color: '#2d6a5e',
  border: '1px solid #c5ddd5',
  borderRadius: 6,
  cursor: 'pointer',
  fontSize: 12,
  fontWeight: 500,
  fontFamily: "'Inter', sans-serif",
  transition: 'background 0.15s',
};

// Empty state
const emptyStyle = {
  padding: '64px 24px',
  textAlign: 'center',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
};

const emptyIconStyle = {
  marginBottom: 16,
  opacity: 0.5,
};

const emptyTitleStyle = {
  fontSize: 18,
  fontWeight: 600,
  color: '#1a3a3a',
  margin: '0 0 8px',
  fontFamily: "'Inter', sans-serif",
};

const emptyDescStyle = {
  fontSize: 14,
  color: '#7a9a92',
  margin: '0 0 20px',
  fontFamily: "'Inter', sans-serif",
};

const emptyTextStyle = {
  fontSize: 14,
  color: '#7a9a92',
  fontFamily: "'Inter', sans-serif",
};